import { analyticsAPI } from "@/lib/api";
import { safeGetItem } from "@/lib/safeStorage";
import { mergeAttribution } from "./attribution";

// Controla onde o Meta Pixel pode existir. Regras do pedido:
// - Nunca inicializar globalmente (removido do index.html).
// - Só carrega em rotas PÚBLICAS de marketing.
// - Nunca em /paciente, /medico, /admin, lobby, chat, prontuário, consulta,
//   documentos, prescrições ou qualquer área autenticada/clínica.
// - /checkout usa inicialização MÍNIMA e EXPLÍCITA, só para checkout_viewed
//   (sem PageView automático, sem advanced matching automático, sem captura
//   de formulário, sem query string, sem dado de paciente).

// Produção real: blinkmed.com.br e o redirect oficial www.blinkmed.com.br.
// Qualquer outro host (preview, domínio antigo, IP, localhost) não carrega Pixel.
function isProductionHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "blinkmed.com.br" || host === "www.blinkmed.com.br";
}

// /obrigado adicionada 15/09/2026 — página de confirmação pós-pagamento
// (PIX/cartão), precisa carregar Pixel+GTM pra disparar o evento de conversão
// (purchase) que alimenta o Meta Ads e o GA4 via GTM. Decisão do dono: tratar
// como rota pública plena (não modo "checkout" mínimo), já que aqui o valor
// real da compra já está confirmado e disponível.
// /inicio adicionada 22/10/2026 — landing 2 do Google Ads; precisa do GTM para
// as conversões (gclid/gbraid já são capturados por lib/attribution.ts).
// /programa-01 adicionada 04/11/2026 — LP do Programa (doc do Paulo). /emagrecimento fica de fora: só redireciona.
const PUBLIC_MARKETING_PATHS = ["/", "/acessar", "/obrigado", "/inicio", "/programa-01"];

function isPublicMarketingPath(pathname: string): boolean {
  if (PUBLIC_MARKETING_PATHS.includes(pathname)) return true;
  if (pathname.startsWith("/termos/")) return true;
  return false;
}

function isCheckoutPath(pathname: string): boolean {
  return pathname === "/checkout" || pathname === "/programa-01/checkout";
}

// Rotas onde o Pixel NUNCA pode existir, mesmo que alguém adicione um path
// novo por engano nas duas listas acima — checagem defensiva por prefixo.
const BLOCKED_PREFIXES = [
  "/paciente", "/medico", "/admin", "/marketing", "/nutricionista", "/intel", "/consulta", "/sala-espera", "/chat",
  "/prontuario",
];

function isBlockedPath(pathname: string): boolean {
  return BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Chamar em rotas públicas de marketing normais. Inicializa o Pixel com
 * correspondência avançada automática (padrão do Meta) e permite PageView.
 */
export function ensurePixelForPublicPage() {
  // NO-OP desde 13/10/2026: o Pixel próprio foi removido (ver bloco "Pixel
  // próprio REMOVIDO" abaixo). Quem carrega e inicializa o Pixel oficial
  // (2952815868431460) é o GTM. A função continua existindo — e sendo chamada
  // pelo router — só para não reescrever os pontos de chamada; se um dia o
  // carregamento voltar a ser responsabilidade do código, é aqui que entra.
}

/**
 * Chamar SÓ em /checkout. Inicialização mínima e explícita: desativa
 * correspondência avançada automática e captura de formulário, e nunca
 * dispara PageView — só checkout_viewed é permitido nesta rota.
 */
export function ensurePixelForCheckout() {
  // NO-OP — mesma razão do `ensurePixelForPublicPage` acima.
}

// ── Os 4 eventos permitidos à Meta (Pixel + CAPI) ──────────────────────────
// Qualquer outro evento de negócio (cadastro, pagamento, consulta,
// agendamento, emergência, chat, fila, médico, prontuário, prescrição) NUNCA
// deve chamar essas funções — segue só para o analytics interno via
// trackInternalEvent() em analytics.ts.

function newEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function currentFbCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === "undefined") return {};
  const match = (name: string) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return {
    fbp: match("_fbp")?.[1],
    fbc: match("_fbc")?.[1],
  };
}

// Envia o par CAPI espelhando o mesmo event_id do Pixel — a Meta deduplica
// automaticamente quando os dois lados usam o mesmo (event_name, event_id).
function sendCapiMirror(eventName: string, eventId: string, extra?: { value?: number; currency?: string }) {
  const { fbp, fbc } = currentFbCookies();
  analyticsAPI.metaCapiEvent({
    event_name: eventName,
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
    fbp,
    fbc,
    ...extra,
  }).catch(() => { /* tracking nunca pode quebrar o site */ });
}

/**
 * ── Pixel próprio REMOVIDO (13/10/2026, pedido da agência) ────────────────
 * Havia DOIS Pixels no site: o `3663834440436818` fixo no bundle (carregava o
 * fbevents.js e disparava PageView/ViewContent/public_cta_click/checkout_viewed)
 * e o oficial `2952815868431460`, carregado pelo GTM. Como `fbq('track')` envia
 * para TODOS os Pixels já inicializados, os eventos do código caíam também no
 * oficial com `eventID` próprio — PageView duplicado em toda página pública e
 * dados misturados entre os dois.
 *
 * Agora o código **não carrega nem inicializa Pixel nenhum**: publica os eventos
 * no `dataLayer` com o MESMO `event_id` que vai para a CAPI (permitindo a
 * deduplicação da Meta), e quem dispara para o Pixel é o GTM, configurado pela
 * agência.
 */
function pushDataLayer(evento: string, dados: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: evento, ...dados });
}

/** Disparar UMA vez por carregamento real de página pública (não em cada render). */
export function trackPageView() {
  if (!isProductionHost()) return;
  pushDataLayer("page_view_blink", { event_id: newEventId() });
}

/** Disparar UMA vez quando o conteúdo principal da landing estiver visível. */
export function trackViewContent() {
  if (!isProductionHost()) return;
  pushDataLayer("view_content", { event_id: newEventId() });
}

/** Disparar SÓ no clique real do CTA público ("Consultar agora" ou equivalente). */
export function trackPublicCtaClick() {
  if (!isProductionHost()) return;
  pushDataLayer("public_cta_click", { event_id: newEventId() });
}

/** checkout_viewed — dataLayer + espelho na CAPI com o MESMO event_id (dedup). */
export function trackCheckoutViewed(params: { value: number; currency: string }) {
  if (!isProductionHost()) return;
  const eventId = newEventId();
  pushDataLayer("checkout_viewed", {
    event_id: eventId,
    value: params.value,
    currency: params.currency,
  });
  sendCapiMirror("checkout_viewed", eventId, params);
}

// ── Programa (04/11/2026, doc do Paulo; prefixo pg01_, neutro) ───────────────────
// Os nomes são exatamente os que as tags do GTM filtram: não mudar. Nunca colocar aqui resposta do questionário,
// peso, altura, nome de remédio, nome, telefone, e-mail ou CPF. O analytics interno segue à parte, em analytics.ts.
// O evento de lead (pedido 4) espera o parecer do jurídico e a caixinha de consentimento: não existe aqui de propósito.

function pushPg01(evento: string, dados: Record<string, unknown> = {}, eventId = newEventId()) {
  if (!isProductionHost()) return;
  pushDataLayer(evento, { event_id: eventId, ...dados });
}

export const trackPg01PageView = () => pushPg01("pg01_page-view");
export const trackPg01FormStart = () => pushPg01("pg01_form-start");
export const trackPg01FormStep = (stepNumber: number) => pushPg01("pg01_form-step", { step_number: stepNumber });
export const trackPg01FormComplete = () => pushPg01("pg01_form-complete");

const CTA_LOCATION: Record<string, string> = { hero: "topo", cartao: "preco", fechamento: "final", header: "header" };
export const trackPg01CtaClick = (onde: string) => pushPg01("pg01_cta-click", { cta_location: CTA_LOCATION[onde] || "topo" });

/** dataLayer + API de Conversões com o MESMO event_id. */
export function trackPg01CheckoutViewed(value: number) {
  if (!isProductionHost()) return;
  const eventId = newEventId();
  pushPg01("pg01_checkout-viewed", { value, currency: "BRL" }, eventId);
  sendCapiMirror("pg01_checkout-viewed", eventId, { value, currency: "BRL" });
}

export const trackPg01PaymentSelected = (paymentMethod: "card" | "pix" | "pixauto", installments: number) =>
  pushPg01("pg01_payment-selected", { payment_method: paymentMethod, installments });

/** Um único push por pedido; event_id = order_id deduplica com a compra que o n8n manda à Meta. Só navegador, sem CAPI. */
export function trackPg01PaymentConfirmed(p: { orderId: string; value: number; paymentMethod: "card" | "pix" | "pixauto"; installments: number }) {
  if (!isProductionHost()) return;
  const chave = `pg01_confirmed_${p.orderId}`;
  try {
    if (sessionStorage.getItem(chave)) return;
    sessionStorage.setItem(chave, "1");
  } catch { /* sem storage: envia assim mesmo */ }
  pushPg01("pg01_payment-confirmed", {
    transaction_id: p.orderId, value: p.value, currency: "BRL", payment_method: p.paymentMethod, installments: p.installments,
  }, p.orderId);
}

/**
 * Cadastro público em `/paciente?modo=cadastro` (13/10/2026, pedido da agência).
 *
 * `/paciente` está na lista de bloqueados porque é a área LOGADA (dados de
 * saúde). Só que a classificação olhava apenas o `pathname`, e é justamente em
 * `/paciente?modo=cadastro` — sem login — que as campanhas caem. Resultado: a
 * tela de cadastro ficava sem GTM, como se fosse área logada, e a conversão se
 * perdia.
 *
 * Libera APENAS esse caso: path exatamente `/paciente`, com `modo=cadastro` e
 * **sem sessão**. Qualquer `/paciente/<subrota>` e qualquer visita já logada
 * continuam bloqueadas.
 */
function isCadastroPublico(pathname: string, search?: string): boolean {
  if (pathname !== "/paciente") return false;
  const qs = search ?? (typeof window !== "undefined" ? window.location.search : "");
  if (new URLSearchParams(qs).get("modo") !== "cadastro") return false;
  // Logado = área logada, mesmo com ?modo=cadastro na URL.
  try {
    return !safeGetItem("clinica_token");
  } catch {
    return true; // storage bloqueado: trata como visitante
  }
}

export function isPixelAllowedOnPath(pathname: string, search?: string): "public" | "checkout" | "blocked" {
  if (isCadastroPublico(pathname, search)) return "public";
  if (isBlockedPath(pathname)) return "blocked";
  if (isCheckoutPath(pathname)) return "checkout";
  if (isPublicMarketingPath(pathname)) return "public";
  return "blocked";
}

// Reexport pra quem só precisa saber se está numa rota pública (SeoHead etc.)
export { mergeAttribution };
